import { NgModule, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { HttpClientModule } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppConfig } from './app-config';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { RaygunErrorHandler } from './app.raygun.setup';
import { AppRoutingModule } from './app-routing.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { APP_BASE_HREF } from '@angular/common';
import { cmsBaseHref } from './base-href';
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { FeatureFlagsService } from './core/feature-flags/feature-flag.service';
import { MatLegacyAutocompleteModule as MatAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { ChunckFailedHandler } from './chunck-failed-handler';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { SectionHeaderComponent } from './shared/standalone/components/section-header/section-header.component';
import {
  CitywireInternalAccountsAngularModule,
  CitywireInternalAccountsSettingsService,
  CitywireInternalAccountsService,
} from '@tpapa/citywire-internal-accounts-angular';
import { TopbarComponent } from './core/topbar/topbar.component';
import { MatMenuModule } from '@angular/material/menu';
import { ToastrModule } from 'ngx-toastr';

@NgModule({
  declarations: [AppComponent, TopbarComponent],
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    AppRoutingModule,
    CoreModule,
    BrowserAnimationsModule,
    EditorModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatTooltipModule,
    MatSnackBarModule,
    SectionHeaderComponent,
    CitywireInternalAccountsAngularModule,
    MatMenuModule,
    ToastrModule.forRoot({
      disableTimeOut: true,
      tapToDismiss: true,
      maxOpened: 1,
      autoDismiss: true,
      positionClass: 'toast-bottom-right',
      preventDuplicates: true,
    }),
  ],
  providers: [
    AppConfig,
    RaygunErrorHandler,
    {
      provide: APP_BASE_HREF,
      useFactory: () => cmsBaseHref(window.location.pathname),
    },
    { provide: ErrorHandler, useClass: ChunckFailedHandler },
    { provide: ErrorHandler, useClass: RaygunErrorHandler },
    {
      provide: APP_INITIALIZER,
      useFactory: initApp,
      deps: [
        AppConfig,
        FeatureFlagsService,
        CitywireInternalAccountsSettingsService,
        CitywireInternalAccountsService,
      ],
      multi: true,
    },
    { provide: 'Window', useValue: window },
    { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}

export function initApp(
  config: AppConfig,
  featureFlagService: FeatureFlagsService,
  accountsSettingsService: CitywireInternalAccountsSettingsService,
  authSettingsService: CitywireInternalAccountsService
): () => Promise<void> {
  {
    return () =>
      config
        .load()
        .then((_) =>
          accountsSettingsService.setConfig({
            providerName: config.authProviderName,
            interceptorSettings: config.inteceptorSettings,
            amplifyConfig: config.amplifyConfig,
            cloudfrontUrl: config.cloudfrontUrl,
          })
        )
        .then((_) => authSettingsService.load())
        .then((_) => featureFlagService.load());
  }
}
